import React, { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { Loader2, Upload, Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { DatePicker } from '@/components/shared/DatePicker'
import { employeeService } from '@/services/employeeService'
import { documentService } from '@/services/documentService'

const ProfileForm = ({ onSuccess }) => {
  const [loading, setLoading] = useState(false)
  const [profileData, setProfileData] = useState(null)
  const [documents, setDocuments] = useState([])
  const [sameAsResidence, setSameAsResidence] = useState(false)
  const [workExperiences, setWorkExperiences] = useState([])
  const fileInputRef = useRef(null)

  const form = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      contact_number: '',
      date_of_birth: undefined,
      gender: '',
      marital_status: '',
      emergency_contact_name: '',
      emergency_contact_number: '',
      highest_qualification: '',
      university: '',
      education_start_date: undefined,
      education_end_date: undefined,
      skills: '',
      account_holder_name: '',
      bank_name: '',
      account_number: '',
      ifsc_code: '',
      pan_number: '',
      residence_address_line_1: '',
      residence_address_line_2: '',
      residence_city: '',
      residence_state: '',
      residence_pincode: '',
      residence_country: 'India',
      current_address_line_1: '',
      current_address_line_2: '',
      current_city: '',
      current_state: '',
      current_pincode: '',
      current_country: 'India',
    },
  })

  // Fetch current profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const [profileRes, docsRes] = await Promise.all([
          employeeService.getMyProfile(),
          documentService.getMyDocuments()
        ])
        
        const employee = profileRes?.data?.employee
        setProfileData(employee)
        setDocuments(docsRes?.data?.documents || [])
        setWorkExperiences(employee?.workExperiences || [])
        
        if (employee) {
          const residenceAddress = employee.addresses?.find(a => a.address_type === 'residence') || {}
          const currentAddress = employee.addresses?.find(a => a.address_type === 'current') || {}

          form.reset({
            first_name: employee.first_name || '',
            last_name: employee.last_name || '',
            contact_number: employee.contact_number || '',
            date_of_birth: employee.date_of_birth ? new Date(employee.date_of_birth) : undefined,
            gender: employee.gender || '',
            marital_status: employee.marital_status || '',
            emergency_contact_name: employee.emergency_contact_name || '',
            emergency_contact_number: employee.emergency_contact_number || '',
            highest_qualification: employee.highest_qualification || '',
            university: employee.university || '',
            education_start_date: employee.education_start_date ? new Date(employee.education_start_date) : undefined,
            education_end_date: employee.education_end_date ? new Date(employee.education_end_date) : undefined,
            skills: employee.skills || '',
            account_holder_name: employee.account_holder_name || '',
            bank_name: employee.bank_name || '',
            account_number: employee.account_number || '',
            ifsc_code: employee.ifsc_code || '',
            pan_number: employee.pan_number || '',
            residence_address_line_1: residenceAddress.address_line_1 || '',
            residence_address_line_2: residenceAddress.address_line_2 || '',
            residence_city: residenceAddress.city || '',
            residence_state: residenceAddress.state || '',
            residence_pincode: residenceAddress.pincode || '',
            residence_country: residenceAddress.country || 'India',
            current_address_line_1: currentAddress.address_line_1 || '',
            current_address_line_2: currentAddress.address_line_2 || '',
            current_city: currentAddress.city || '',
            current_state: currentAddress.state || '',
            current_pincode: currentAddress.pincode || '',
            current_country: currentAddress.country || 'India',
          })
        }
      } catch (error) {
        console.error('Error fetching profile:', error)
        toast.error('Failed to load profile data')
      }
    }
    
    fetchProfile()
  }, [form])

  const handleSameAsResidence = (checked) => {
    setSameAsResidence(checked)
    if (checked) {
      const values = form.getValues()
      form.setValue('current_address_line_1', values.residence_address_line_1)
      form.setValue('current_address_line_2', values.residence_address_line_2)
      form.setValue('current_city', values.residence_city)
      form.setValue('current_state', values.residence_state)
      form.setValue('current_pincode', values.residence_pincode)
      form.setValue('current_country', values.residence_country)
    }
  }

  const addWorkExperience = () => {
    setWorkExperiences([
      ...workExperiences,
      {
        organization_name: '',
        position: '',
        start_date: '',
        end_date: '',
        location: '',
        description: ''
      }
    ])
  }

  const removeWorkExperience = (index) => {
    setWorkExperiences(workExperiences.filter((_, i) => i !== index))
  }

  const updateWorkExperience = (index, field, value) => {
    const updated = [...workExperiences]
    updated[index] = { ...updated[index], [field]: value }
    setWorkExperiences(updated)
  }

  const onSubmit = async (values) => {
    setLoading(true)
    try {
      // Build addresses array
      const addresses = []
      
      if (values.residence_address_line_1) {
        addresses.push({
          address_type: 'residence',
          address_line_1: values.residence_address_line_1,
          address_line_2: values.residence_address_line_2,
          city: values.residence_city,
          state: values.residence_state,
          pincode: values.residence_pincode,
          country: values.residence_country,
        })
      }
      
      if (values.current_address_line_1 && !sameAsResidence) {
        addresses.push({
          address_type: 'current',
          address_line_1: values.current_address_line_1,
          address_line_2: values.current_address_line_2,
          city: values.current_city,
          state: values.current_state,
          pincode: values.current_pincode,
          country: values.current_country,
        })
      } else if (sameAsResidence && values.residence_address_line_1) {
        addresses.push({
          address_type: 'current',
          address_line_1: values.residence_address_line_1,
          address_line_2: values.residence_address_line_2,
          city: values.residence_city,
          state: values.residence_state,
          pincode: values.residence_pincode,
          country: values.residence_country,
        })
      }

      const formattedData = {
        first_name: values.first_name,
        last_name: values.last_name,
        contact_number: values.contact_number,
        date_of_birth: values.date_of_birth ? dayjs(values.date_of_birth).format('YYYY-MM-DD') : null,
        gender: values.gender,
        marital_status: values.marital_status,
        emergency_contact_name: values.emergency_contact_name,
        emergency_contact_number: values.emergency_contact_number,
        highest_qualification: values.highest_qualification,
        university: values.university,
        education_start_date: values.education_start_date ? dayjs(values.education_start_date).format('YYYY-MM-DD') : null,
        education_end_date: values.education_end_date ? dayjs(values.education_end_date).format('YYYY-MM-DD') : null,
        skills: values.skills,
        account_holder_name: values.account_holder_name,
        bank_name: values.bank_name,
        account_number: values.account_number,
        ifsc_code: values.ifsc_code,
        pan_number: values.pan_number,
        addresses,
        workExperiences: workExperiences.map(exp => ({
          ...exp,
          start_date: exp.start_date ? dayjs(exp.start_date).format('YYYY-MM-DD') : null,
          end_date: exp.end_date ? dayjs(exp.end_date).format('YYYY-MM-DD') : null,
        })),
      }

      const response = await employeeService.updateMyProfile(formattedData)
      
      if (response.success) {
        toast.success('Profile updated successfully!')
        onSuccess?.()
      }
    } catch (error) {
      console.error('Update error:', error)
      toast.error(error.response?.data?.error || 'Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
  ]

  const maritalStatusOptions = [
    { value: 'single', label: 'Single' },
    { value: 'married', label: 'Married' },
    { value: 'divorced', label: 'Divorced' },
    { value: 'widowed', label: 'Widowed' },
  ]

  const qualificationOptions = [
    { value: 'high_school', label: 'High School' },
    { value: 'diploma', label: 'Diploma' },
    { value: 'bachelor', label: "Bachelor's Degree" },
    { value: 'master', label: "Master's Degree" },
    { value: 'phd', label: 'PhD' },
    { value: 'other', label: 'Other' },
  ]

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      <Accordion type="multiple" defaultValue={['personal', 'address', 'education', 'bank']} className="space-y-4">
        {/* Personal Information */}
        <AccordionItem value="personal" className="border rounded-lg">
          <AccordionTrigger className="px-4">Personal Information</AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input {...form.register('first_name')} placeholder="First name" />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input {...form.register('last_name')} placeholder="Last name" />
              </div>
              <div className="space-y-2">
                <Label>Contact Number</Label>
                <Input {...form.register('contact_number')} placeholder="Contact number" />
              </div>
              <div className="space-y-2">
                <Label>Date of Birth</Label>
                <DatePicker
                  value={form.watch('date_of_birth')}
                  onChange={(date) => form.setValue('date_of_birth', date)}
                  placeholder="Select date"
                />
              </div>
              <div className="space-y-2">
                <Label>Gender</Label>
                <Select value={form.watch('gender')} onValueChange={(v) => form.setValue('gender', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    {genderOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Marital Status</Label>
                <Select value={form.watch('marital_status')} onValueChange={(v) => form.setValue('marital_status', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {maritalStatusOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Emergency Contact Name</Label>
                <Input {...form.register('emergency_contact_name')} placeholder="Emergency contact name" />
              </div>
              <div className="space-y-2">
                <Label>Emergency Contact Number</Label>
                <Input {...form.register('emergency_contact_number')} placeholder="Emergency contact number" />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Address */}
        <AccordionItem value="address" className="border rounded-lg">
          <AccordionTrigger className="px-4">Address Information</AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="space-y-6">
              <div>
                <h4 className="font-medium mb-4">Residence Address</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label>Address Line 1</Label>
                    <Input {...form.register('residence_address_line_1')} placeholder="Address line 1" />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Address Line 2</Label>
                    <Input {...form.register('residence_address_line_2')} placeholder="Address line 2" />
                  </div>
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input {...form.register('residence_city')} placeholder="City" />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input {...form.register('residence_state')} placeholder="State" />
                  </div>
                  <div className="space-y-2">
                    <Label>Pincode</Label>
                    <Input {...form.register('residence_pincode')} placeholder="Pincode" />
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Input {...form.register('residence_country')} placeholder="Country" />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="sameAsResidence"
                  checked={sameAsResidence}
                  onCheckedChange={handleSameAsResidence}
                />
                <Label htmlFor="sameAsResidence">Current address same as residence</Label>
              </div>

              {!sameAsResidence && (
                <div>
                  <h4 className="font-medium mb-4">Current Address</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <Label>Address Line 1</Label>
                      <Input {...form.register('current_address_line_1')} placeholder="Address line 1" />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Address Line 2</Label>
                      <Input {...form.register('current_address_line_2')} placeholder="Address line 2" />
                    </div>
                    <div className="space-y-2">
                      <Label>City</Label>
                      <Input {...form.register('current_city')} placeholder="City" />
                    </div>
                    <div className="space-y-2">
                      <Label>State</Label>
                      <Input {...form.register('current_state')} placeholder="State" />
                    </div>
                    <div className="space-y-2">
                      <Label>Pincode</Label>
                      <Input {...form.register('current_pincode')} placeholder="Pincode" />
                    </div>
                    <div className="space-y-2">
                      <Label>Country</Label>
                      <Input {...form.register('current_country')} placeholder="Country" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Education */}
        <AccordionItem value="education" className="border rounded-lg">
          <AccordionTrigger className="px-4">Education</AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Highest Qualification</Label>
                <Select value={form.watch('highest_qualification')} onValueChange={(v) => form.setValue('highest_qualification', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select qualification" />
                  </SelectTrigger>
                  <SelectContent>
                    {qualificationOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>University/Institution</Label>
                <Input {...form.register('university')} placeholder="University name" />
              </div>
              <div className="space-y-2">
                <Label>Start Date</Label>
                <DatePicker
                  value={form.watch('education_start_date')}
                  onChange={(date) => form.setValue('education_start_date', date)}
                  placeholder="Start date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <DatePicker
                  value={form.watch('education_end_date')}
                  onChange={(date) => form.setValue('education_end_date', date)}
                  placeholder="End date"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Skills</Label>
                <Textarea {...form.register('skills')} placeholder="Enter your skills (comma separated)" />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Bank Details */}
        <AccordionItem value="bank" className="border rounded-lg">
          <AccordionTrigger className="px-4">Bank Details</AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Account Holder Name</Label>
                <Input {...form.register('account_holder_name')} placeholder="Account holder name" />
              </div>
              <div className="space-y-2">
                <Label>Bank Name</Label>
                <Input {...form.register('bank_name')} placeholder="Bank name" />
              </div>
              <div className="space-y-2">
                <Label>Account Number</Label>
                <Input {...form.register('account_number')} placeholder="Account number" />
              </div>
              <div className="space-y-2">
                <Label>IFSC Code</Label>
                <Input {...form.register('ifsc_code')} placeholder="IFSC code" />
              </div>
              <div className="space-y-2">
                <Label>PAN Number</Label>
                <Input {...form.register('pan_number')} placeholder="PAN number" />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Work Experience */}
        <AccordionItem value="experience" className="border rounded-lg">
          <AccordionTrigger className="px-4">Work Experience</AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="space-y-4">
              {workExperiences.map((exp, index) => (
                <Card key={index}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h5 className="font-medium">Experience {index + 1}</h5>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeWorkExperience(index)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Organization</Label>
                        <Input
                          value={exp.organization_name}
                          onChange={(e) => updateWorkExperience(index, 'organization_name', e.target.value)}
                          placeholder="Organization name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Position</Label>
                        <Input
                          value={exp.position}
                          onChange={(e) => updateWorkExperience(index, 'position', e.target.value)}
                          placeholder="Position/Role"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Start Date</Label>
                        <DatePicker
                          value={exp.start_date ? new Date(exp.start_date) : undefined}
                          onChange={(date) => updateWorkExperience(index, 'start_date', date)}
                          placeholder="Start date"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>End Date</Label>
                        <DatePicker
                          value={exp.end_date ? new Date(exp.end_date) : undefined}
                          onChange={(date) => updateWorkExperience(index, 'end_date', date)}
                          placeholder="End date (leave empty if current)"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Location</Label>
                        <Input
                          value={exp.location || ''}
                          onChange={(e) => updateWorkExperience(index, 'location', e.target.value)}
                          placeholder="Location"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Description</Label>
                        <Textarea
                          value={exp.description || ''}
                          onChange={(e) => updateWorkExperience(index, 'description', e.target.value)}
                          placeholder="Job description"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Button type="button" variant="outline" onClick={addWorkExperience}>
                <Plus className="mr-2 h-4 w-4" />
                Add Work Experience
              </Button>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="flex justify-end gap-4">
        <Button type="submit" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>
    </form>
  )
}

export default ProfileForm
